import {
  FlaskConical,
  Code2,
  Megaphone,
  Zap,
  LineChart,
  Palette,
  Globe2,
  FileText,
  Handshake,
  LifeBuoy,
  Scale,
  Workflow,
  HeartPulse,
  Users,
  Rocket,
} from 'lucide-react';
// Legacy skill sources inlined below (previously in claudeSkillsData.tsx and skillsData.js)
// to provide a single, static source of truth in this file.
const SKILLS_DATA: any[] = [
  { id: "web-search",     name: "Web Search",     icon: 'Search',        category: "research",      description: "Search the web via DuckDuckGo. No API key needed.", installed: true  },
  { id: "url-scraper",    name: "URL Scraper",     icon: 'Globe',         category: "research",      description: "Fetch and parse any web page into clean text.",      installed: true  },
  { id: "file-processor", name: "File Processor",  icon: 'FileText',      category: "data",          description: "Read, write and parse files — TXT, JSON, CSV, MD.",  installed: true  },
  { id: "data-parser",    name: "Data Parser",     icon: 'Database',      category: "data",          description: "Parse CSV / JSON / XML into structured objects.",     installed: true  },
  { id: "google-calendar",name: "Google Calendar", icon: 'Calendar',      category: "productivity",  description: "Read events, create bookings and check availability.", installed: false },
  { id: "pm-projects",    name: "Project Manager", icon: 'FolderKanban',  category: "productivity",  description: "Create and manage projects in WorKult Project Manager.", installed: true },
  { id: "pm-tasks",       name: "Task Manager",    icon: 'CheckSquare',   category: "productivity",  description: "Create, update and query tasks across all WorKult projects.", installed: true },
  { id: "pm-ideas",       name: "Idea Notebook",   icon: 'Lightbulb',     category: "productivity",  description: "Capture, update and query ideas/notes in WorKult.", installed: true },
  { id: "gmail",          name: "Gmail",           icon: 'Mail',          category: "communication", description: "Read, send and search Gmail.",                        installed: true  },
  { id: 'lyra:gmail-reader', name: 'Gmail Reader', icon: 'Mail', category: 'communication', description: 'List and summarize unread Gmail messages from your connected account.', installed: false },
  { id: 'lyra:calendar-assistant', name: 'Calendar Assistant', icon: 'CalendarDays', category: 'productivity', description: 'Read and manage Google Calendar events for scheduling and availability checks.', installed: false },
  { id: 'lyra:teams-connector', name: 'Microsoft Teams Connector', icon: 'Handshake', category: 'communication', description: 'Read channel messages and post notifications to Microsoft Teams channels.', installed: false },
  { id: "telegram",       name: "Telegram",        icon: 'Send',          category: "communication", description: "Send messages, broadcast to groups, receive via webhook.", installed: true },
  { id: "whatsapp",       name: "WhatsApp",        icon: 'MessageCircle', category: "communication", description: "Send messages and trigger agents via WhatsApp Business API.", installed: true },
  { id: "kult-memory",    name: "KultMemory",      icon: 'Brain',         category: "memory",        description: "Persistent vector memory across all agent runs.",     installed: true  },
];

const CLAUDE_SKILLS_DATA: any[] = [
  { id:"docx",            name:"Word Documents",       cat:"documents",     source:"official",  desc:"Create, edit, and analyze .docx files with tracked changes, comments, and formatting",      repo:"https://github.com/anthropics/skills/tree/main/skills/docx",                  prompt:"You have access to a Word document skill. You can create, edit, and analyze .docx files including tracked changes, comments, rich formatting, and text extraction." },
  { id:"pdf",             name:"PDF Toolkit",          cat:"documents",     source:"official",  desc:"Extract text/tables, create, merge, and split PDFs; handle fillable forms",              repo:"https://github.com/anthropics/skills/tree/main/skills/pdf",                   prompt:"You have a PDF skill for extracting text and tables, creating PDFs, merging/splitting documents, and handling fillable PDF forms." },
  { id:"pptx",            name:"PowerPoint",           cat:"documents",     source:"official",  desc:"Create and edit presentations with layouts, templates, charts, and automated slides",      repo:"https://github.com/anthropics/skills/tree/main/skills/pptx",                  prompt:"You have a PowerPoint skill to create and edit .pptx files with layouts, templates, charts, and automated slide generation." },
  { id:"xlsx",            name:"Excel Spreadsheets",   cat:"documents",     source:"official",  desc:"Create and analyze spreadsheets with formulas, data analysis, and visualization",          repo:"https://github.com/anthropics/skills/tree/main/skills/xlsx",                  prompt:"You have an Excel skill for creating and editing .xlsx spreadsheets with formulas, data analysis, formatting, and chart visualization." },
  { id:"algorithmic-art", name:"Generative Art",        cat:"design",        source:"official",  desc:"Create generative art using p5.js with flow fields and particle systems",                  repo:"https://github.com/anthropics/skills/tree/main/skills/algorithmic-art",        prompt:"You have a generative art skill using p5.js. Create algorithmic art with seeded randomness, flow fields, and particle systems." },
  { id:"canvas-design",   name:"Canvas Design",        cat:"design",        source:"official",  desc:"Design visual art in .png and .pdf formats using strong design principles",                repo:"https://github.com/anthropics/skills/tree/main/skills/canvas-design",          prompt:"You have a canvas design skill to create polished visual art and designs exported as .png or .pdf." },
  { id:"frontend-slides", name:"HTML Slides",           cat:"design",        source:"community", desc:"Animation-rich HTML presentations — from scratch or converted from PowerPoint",            repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You can create animation-rich HTML presentations from scratch or convert PowerPoint files into interactive web-based slide decks with smooth transitions." },
  { id:"web-asset-gen",   name:"Web Asset Generator",  cat:"design",        source:"community", desc:"Generate favicons, app icons, and social media images",                                    repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have a web asset skill for creating favicons, app icons at all standard sizes, Open Graph images, and other web and mobile assets." },
  { id:"frontend-design", name:"Frontend Design",       cat:"development",   source:"official",  desc:"Avoid generic AI aesthetics — make bold, intentional design decisions for React & Tailwind", repo:"https://github.com/anthropics/skills/blob/main/skills/frontend-design",      prompt:"Avoid generic 'AI slop' aesthetics. Make bold, intentional design decisions with strong visual hierarchy, distinct palettes, thoughtful spacing, and tasteful micro-interactions. Best with React & Tailwind." },
  { id:"web-artifacts",   name:"Web Artifacts Builder", cat:"development",   source:"official",  desc:"Build complex HTML artifacts using React, Tailwind CSS, and shadcn/ui",                    repo:"https://github.com/anthropics/skills/tree/main/skills/web-artifacts-builder",  prompt:"You can build complex interactive HTML artifacts using React, Tailwind CSS, and shadcn/ui components directly inside Claude.ai artifacts." },
  { id:"mcp-builder",     name:"MCP Builder",          cat:"development",   source:"official",  desc:"Guide for creating high-quality MCP servers that integrate external APIs",                 repo:"https://github.com/anthropics/skills/tree/main/skills/mcp-builder",            prompt:"You have an MCP builder skill for guiding the creation of high-quality Model Context Protocol servers to integrate external APIs and services." },
  { id:"webapp-testing",  name:"Web App Testing",       cat:"development",   source:"official",  desc:"Test web applications with Playwright for UI verification and debugging",                  repo:"https://github.com/anthropics/skills/tree/main/skills/webapp-testing",         prompt:"You have a Playwright-based web testing skill to test local web applications, verify UI behavior, and debug issues." },
  { id:"playwright",      name:"Browser Automation",    cat:"development",   source:"community", desc:"General-purpose browser automation using Playwright",                                      repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have a Playwright browser automation skill for web scraping, automated testing, and UI interaction across any website." },
  { id:"d3js",            name:"D3.js Visualizations",  cat:"development",   source:"community", desc:"Create interactive data visualizations in D3.js",                                          repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have a D3.js skill for creating interactive charts, network graphs, geographic maps, and custom data visualizations." },
  { id:"shadcn-ui",       name:"shadcn/ui Expert",      cat:"development",   source:"community", desc:"Deep context on shadcn/ui components and consistent pattern enforcement",                  repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have deep shadcn/ui expertise: full API knowledge of every component, variants, and composition patterns, enforcing consistent shadcn usage throughout the codebase." },
  { id:"get-shit-done",   name:"Spec-Driven Dev",       cat:"development",   source:"community", desc:"Meta-prompting, context engineering, and spec-driven development by TÂCHES",               repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"Apply spec-driven development: write a clear spec before coding, use context engineering to stay focused, and clarify requirements via meta-prompting before any implementation." },
  { id:"brand-guidelines",name:"Brand Guidelines",      cat:"communication", source:"official",  desc:"Apply consistent brand colors and typography to every artifact",                           repo:"https://github.com/anthropics/skills/tree/main/skills/brand-guidelines",       prompt:"You have a brand guidelines skill. Apply consistent brand colors, typography, and visual identity to every artifact you create." },
  { id:"internal-comms",  name:"Internal Comms",        cat:"communication", source:"official",  desc:"Write status reports, newsletters, FAQs, and internal documents",                          repo:"https://github.com/anthropics/skills/tree/main/skills/internal-comms",         prompt:"You have an internal communications skill for writing well-structured status reports, team newsletters, FAQs, and org announcements." },
  { id:"ffuf-fuzzing",    name:"Web Fuzzing (ffuf)",    cat:"security",      source:"community", desc:"Expert ffuf guidance for pen testing: authenticated fuzzing, raw requests, auto-calibration", repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",       prompt:"You have expert ffuf web fuzzing knowledge for penetration testing: authenticated fuzzing with raw requests, auto-calibration, wordlist selection, and systematic result analysis." },
  { id:"security-audit",  name:"Security Auditing",     cat:"security",      source:"community", desc:"CodeQL/Semgrep static analysis, code auditing, and vulnerability detection",               repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have advanced security auditing skills: static analysis with CodeQL and Semgrep, variant analysis, systematic code auditing, and structured vulnerability detection." },
  { id:"scientific",      name:"Scientific Computing",  cat:"science",       source:"community", desc:"Comprehensive scientific skills: specialized libraries and scientific databases",           repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have comprehensive scientific computing skills including numpy, scipy, matplotlib, pandas, and integration with scientific databases and domain-specific research libraries." },
  { id:"ios-simulator",   name:"iOS Simulator",         cat:"mobile",        source:"community", desc:"Build, navigate, and test iOS apps through automation",                                    repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have an iOS simulator skill for building, navigating, and testing iOS applications programmatically through automation." },
  { id:"expo",            name:"Expo Development",      cat:"mobile",        source:"community", desc:"Official Expo team skills for React Native Expo app development",                          repo:"https://github.com/travisvn/awesome-claude-skills#individual-skills",          prompt:"You have official Expo development skills for building new cross-platform apps with Expo and EAS." },
];
// ─── Design Tokens ────────────────────────────────────────────────────────────
export const WS = {
  // Backgrounds — neutral gray, matching the app-wide palette used by
  // Agents/AgentDetailsPage/etc. (this used to be a slightly warmer,
  // pre-revamp beige set that no longer matched the rest of the app).
  bg:          'var(--lyra-color-surface)',
  surface:     'var(--lyra-color-surface)',
  surface2:    'var(--lyra-color-surface-2)',
  surface3:    'var(--app-surface-3)',
  overlay:     'rgba(28,28,26,0.45)',

  // Borders
  border:      'var(--lyra-color-border)',
  border2:     'var(--app-border-strong)',
  border3:     '#B8B4AE',

  // Text
  ink:         'var(--lyra-color-text)',
  ink2:        'var(--app-text-2)',
  ink3:        'var(--lyra-color-text-muted)',
  ink4:        'var(--app-text-3)',

  // Accent — forest green (matches the current app shell / other tool pages)
  accent:      'var(--lyra-color-primary)',
  accentHov:   'var(--app-primary-deep)',
  accentBg:    'var(--app-primary-soft)',
  accentBd:    'var(--app-primary-soft-2)',
  accentGlow:  '0 0 0 3px undefined',

  // Green
  green:       'var(--lyra-color-success)',
  greenBg:     'var(--lyra-color-success-bg)',
  greenBd:     'var(--app-success-border)',
  greenText:   '#14532D',

  // Amber
  amber:       'var(--lyra-color-warning)',
  amberBg:     'var(--lyra-color-warning-bg)',
  amberBd:     'var(--app-warning-border)',
  amberText:   'var(--lyra-color-warning)',

  // Violet
  violet:      'var(--lyra-color-primary)',
  violetBg:    'var(--app-primary-soft)',
  violetBd:    'var(--app-primary-soft-2)',
  violetText:  'var(--app-primary-deep)',

  // Red
  red:         'var(--lyra-color-danger)',
  redBg:       'var(--lyra-color-danger-bg)',
  redBd:       'var(--app-danger-border)',
  redText:     '#991B1B',

  // Pink
  pink:        '#BE185D',
  pinkBg:      'var(--lyra-color-surface-2)',
  pinkBd:      '#FBCFE8',
  pinkText:    '#9D174D',

  // Rose
  rose:        '#E11D48',
  roseBg:      'var(--lyra-color-surface-2)',
  roseBd:      '#FECDD3',
  roseText:    '#9F1239',

  // Teal
  teal:        '#0F766E',
  tealBg:      'var(--lyra-color-surface-2)',
  tealBd:      'var(--lyra-color-success-bg)',
  tealText:    '#134E4A',

  // Blue
  blue:        '#1D4ED8',
  blueBg:      'var(--app-primary-soft)',
  blueBd:      'var(--app-primary-soft-2)',
  blueText:    '#1E3A8A',

  // Orange
  orange:      '#C2410C',
  orangeBg:    'var(--lyra-color-warning-bg)',
  orangeBd:    '#FED7AA',
  orangeText:  '#9A3412',

  // Cyan
  cyan:        '#0891B2',
  cyanBg:      'var(--lyra-color-surface-2)',
  cyanBd:      'var(--app-primary-soft)',
  cyanText:    '#164E63',

  // Slate
  slate:       '#475569',
  slateBg:     'var(--lyra-color-surface-2)',
  slateBd:     '#CBD5E1',
  slateText:   '#334155',

  // Lime
  lime:        '#65A30D',
  limeBg:      'var(--lyra-color-surface-2)',
  limeBd:      'var(--lyra-color-success-bg)',
  limeText:    '#3F6212',

  // Sky
  sky:         '#0284C7',
  skyBg:       'var(--app-primary-soft)',
  skyBd:       'var(--app-primary-soft-2)',
  skyText:     'var(--app-primary-deep)',

  // Indigo
  indigo:      'var(--lyra-color-primary)',
  indigoBg:    'var(--app-primary-soft)',
  indigoBd:    '#C7D2FE',
  indigoText:  '#3730A3',

  // Neutrals
  neutral:     '#737373',
  neutralBg:   'var(--lyra-color-surface-2)',
  neutralBd:   'var(--app-surface-3)',
  neutralText: '#404040',

  // Shadows
  shadowXs:    '0 1px 2px rgba(0,0,0,0.04)',
  shadowSm:    '0 2px 6px rgba(0,0,0,0.06)',
  shadowMd:    '0 8px 20px rgba(0,0,0,0.08)',
  shadowLg:    '0 16px 40px rgba(0,0,0,0.12)',

  // Radius
  radiusSm:    '8px',
  radiusMd:    '12px',
  radiusLg:    '16px',
  radiusXl:    '24px',

  // Gradients
  gradientAccent:
    'linear-gradient(135deg, var(--lyra-color-primary) 0%, var(--app-primary-deep) 100%)',

  gradientCard:
    'linear-gradient(135deg, undefined 0%, rgba(156,196,182,0.04) 100%)',

  gradientBlue:
    'linear-gradient(135deg, var(--lyra-color-primary) 0%, #1D4ED8 100%)',

  gradientViolet:
    'linear-gradient(135deg, #8B5CF6 0%, var(--lyra-color-primary) 100%)',

  gradientWarm:
    'linear-gradient(135deg, var(--lyra-color-warning-bg) 0%, var(--lyra-color-warning-bg) 100%)',

  gradientDark:
    'linear-gradient(135deg, var(--lyra-color-text) 0%, var(--lyra-color-text) 100%)',

  // Glass surfaces
  glass:
    'rgba(255,255,255,0.72)',

  glassBorder:
    'rgba(255,255,255,0.28)',

  // Motion
  transitionFast: '120ms ease',
  transition:     '180ms ease',
  transitionSlow: '260ms ease',
};

// ─── Global CSS for Workspace ─────────────────────────────────────────────────
export const WORKSPACE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');

  @keyframes ws-spin    { to { transform: rotate(360deg); } }
  @keyframes ws-fadein  { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
  @keyframes ws-scalein { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }
  @keyframes ws-slide-right { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
  @keyframes ws-shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  ::-webkit-scrollbar { width: 3px; height: 3px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 99px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }

  .ws-input:focus { outline: none !important; border-color: undefined !important; box-shadow: 0 0 0 3px undefined !important; }
  
  input[type="range"] {
    -webkit-appearance: none;
    height: 4px;
    border-radius: 99px;
    background: var(--lyra-color-border);
    cursor: pointer;
  }
  input[type="range"]::-moz-range-track {
    height: 4px;
    border-radius: 99px;
    background: var(--lyra-color-border);
  }
  input[type="range"]::-moz-range-thumb {
    width: 14px; height: 14px;
    border: none;
    border-radius: 50%;
    background: var(--lyra-color-primary);
    box-shadow: 0 0 0 3px undefined;
    cursor: pointer;
  }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px; height: 14px;
    border-radius: 50%;
    background: var(--lyra-color-primary);
    box-shadow: 0 0 0 3px undefined;
    cursor: pointer;
    transition: box-shadow 0.15s;
  }
  input[type="range"]::-webkit-slider-thumb:hover {
    box-shadow: 0 0 0 5px undefined;
  }
  input[type="datetime-local"] { color-scheme: light; }
  input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    filter: invert(0.35) sepia(0.6) saturate(1.4) hue-rotate(95deg);
    cursor: pointer;
  }

  .ws-pill-animate { animation: ws-scalein 0.2s cubic-bezier(0.34,1.56,0.64,1); }
  .ws-card-animate { animation: ws-fadein 0.18s ease; }
  .ws-slide-animate { animation: ws-slide-right 0.2s ease; }

  .ws-skill-card:hover .ws-skill-add { opacity: 1 !important; transform: scale(1) !important; }
  
  .ws-domain-toggle { transition: transform 0.2s cubic-bezier(0.4,0,0.2,1); }
  .ws-domain-toggle.open { transform: rotate(90deg); }
  
  .ws-tab-active {
    background: rgba(99,102,241,0.15);
    color: #818CF8;
    border-color: rgba(99,102,241,0.30) !important;
  }

  .ws-hover-scale:hover { transform: translateY(-1px); }
  .ws-hover-scale { transition: transform 0.15s ease; }

  .ws-gradient-text {
    background: linear-gradient(135deg, var(--lyra-color-primary), var(--app-primary-deep));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .ws-suggested-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .ws-suggested-scroll::-webkit-scrollbar { display: none; }

  .ws-cmd-palette-backdrop {
    animation: ws-fadein 0.15s ease;
  }
  .ws-cmd-palette-box {
    animation: ws-scalein 0.18s cubic-bezier(0.34,1.56,0.64,1);
  }

  select option {
    background: var(--lyra-color-surface);
    color: var(--lyra-color-text);
  }

  .ws-layout-body {
    flex: 1;
    display: grid;
    grid-template-columns: var(--ws-left-width, 260px) minmax(0, 1fr) clamp(340px, 26vw, 440px);
    overflow: hidden;
    min-height: 0;
    transition: grid-template-columns 0.18s ease;
  }

  .ws-layout-pane {
    min-width: 0;
    min-height: 0;
  }

  @media (max-width: 1100px) {
    .ws-layout-body {
      grid-template-columns: 220px minmax(0, 1fr);
      grid-template-areas:
        "left center"
        "right right";
      overflow: auto;
    }
    .ws-layout-pane-left { grid-area: left; }
    .ws-layout-pane-center { grid-area: center; }
    .ws-layout-pane-right {
      grid-area: right;
      border-top: 1px solid var(--lyra-color-border);
      border-left: none !important;
    }
  }

  @media (max-width: 768px) {
    .ws-layout-body {
      display: flex;
      flex-direction: column;
      overflow: auto;
    }
    .ws-layout-pane-left {
      order: 2;
      border-right: none !important;
      border-top: 1px solid var(--lyra-color-border);
    }
    .ws-layout-pane-center { order: 1; }
    .ws-layout-pane-right {
      order: 3;
      border-top: 1px solid var(--lyra-color-border);
    }
  }

  .ws-topbar {
    min-height: 52px;
    height: auto;
  }

  .ws-topbar-title {
    min-width: 0;
  }

  .ws-topbar-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    .ws-topbar {
      padding: 8px 10px !important;
      gap: 8px !important;
      flex-wrap: wrap;
      align-items: center !important;
    }
    .ws-topbar-divider,
    .ws-topbar-kbd {
      display: none !important;
    }
    .ws-topbar-title {
      order: 3;
      width: 100%;
    }
    .ws-topbar-title-text {
      font-size: 13px !important;
    }
    .ws-topbar-save {
      margin-left: auto;
      padding: 7px 12px !important;
      font-size: 12px !important;
    }
  }

  @media (max-width: 520px) {
    .ws-topbar-prefill {
      display: none !important;
    }
    .ws-topbar-save {
      min-width: 108px;
      justify-content: center;
    }
  }

  .ws-center-inner {
    width: 100%;
  }

  @media (max-width: 768px) {
    .ws-center-inner {
      padding: 14px 12px 28px !important;
    }
    .ws-center-name-row {
      flex-direction: column;
      align-items: stretch !important;
      gap: 8px !important;
    }
    .ws-center-conflict-pill {
      align-self: flex-start;
      white-space: normal !important;
    }
    .ws-center-exec-row {
      flex-direction: column;
      align-items: flex-start !important;
    }
    .ws-center-exec-scroll {
      width: 100%;
    }
    .ws-center-toolbar {
      flex-wrap: wrap;
      align-items: center !important;
      gap: 6px !important;
      padding: 10px 10px !important;
    }
    .ws-center-token-chip {
      width: 100%;
      justify-content: space-between;
      order: 5;
    }
  }
`;

// ─── Skill Library Domain Groups ────────────────────────────────────────────── 
export interface SkillItem {
  id: string;
  domain: string;
  icon: string; // Lucide icon name
  name: string;
  description: string;
  tags: string[];
  uses: string;
  official: boolean;
  prompt?: string;
  source?: string; // optional: e.g. 'workspace' | 'server' | 'claude'
  lyraSkill?: boolean; // built-in Lyra executable skill
  templateCount?: number; // templates available from LyraSkills for this skill
}

export const SKILL_DOMAINS = [
  {
    id: 'research',
    label: 'Research',
    emoji: '🔬',
    icon: FlaskConical,
    color: WS.teal,
    colorBg: WS.tealBg,
  },
  {
    id: 'coding',
    label: 'Coding',
    emoji: '💻',
    icon: Code2,
    color: WS.accent,
    colorBg: WS.accentBg,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    emoji: '📣',
    icon: Megaphone,
    color: WS.amber,
    colorBg: WS.amberBg,
  },
  {
    id: 'automation',
    label: 'Automation',
    emoji: '⚡',
    icon: Zap,
    color: WS.green,
    colorBg: WS.greenBg,
  },
  {
    id: 'finance',
    label: 'Finance',
    emoji: '📈',
    icon: LineChart,
    color: WS.blue,
    colorBg: WS.blueBg,
  },
  {
    id: 'design',
    label: 'Design',
    emoji: '🎨',
    icon: Palette,
    color: WS.pink,
    colorBg: WS.pinkBg,
  },
  {
    id: 'social',
    label: 'Social Media',
    emoji: '🌐',
    icon: Globe2,
    color: WS.violet,
    colorBg: WS.violetBg,
  },
  {
    id: 'documents',
    label: 'Document AI',
    emoji: '📄',
    icon: FileText,
    color: WS.orange,
    colorBg: WS.orangeBg,
  },
  {
    id: 'sales',
    label: 'Sales',
    emoji: '💼',
    icon: Handshake,
    color: WS.red,
    colorBg: WS.redBg,
  },
  {
    id: 'support',
    label: 'Support',
    emoji: '🎧',
    icon: LifeBuoy,
    color: WS.cyan,
    colorBg: WS.cyanBg,
  },
  {
    id: 'legal',
    label: 'Legal',
    emoji: '⚖️',
    icon: Scale,
    color: WS.slate,
    colorBg: WS.slateBg,
  },
  {
    id: 'operations',
    label: 'Operations',
    emoji: '⚙️',
    icon: Workflow,
    color: WS.lime,
    colorBg: WS.limeBg,
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    emoji: '🩺',
    icon: HeartPulse,
    color: WS.rose,
    colorBg: WS.roseBg,
  },
  {
    id: 'hr',
    label: 'HR',
    emoji: '👥',
    icon: Users,
    color: WS.sky,
    colorBg: WS.skyBg,
  },
  {
    id: 'founders',
    label: 'Founders',
    emoji: '🚀',
    icon: Rocket,
    color: WS.indigo,
    colorBg: WS.indigoBg,
  },
];

export const SKILL_LIBRARY_BASE: SkillItem[] = [
 
  // ═══════════════════════════════════════════
  // RESEARCH
  // ═══════════════════════════════════════════
 
  {
    id: 'web-search',
    domain: 'research',
    icon: 'Search',
    name: 'Web Search',
    description: 'Search the web via DuckDuckGo. No API key needed.',
    tags: ['search', 'live'],
    uses: '12.4k',
    official: true,
    prompt:
      'You are a research assistant with real-time web search capability. Find accurate, up-to-date information, cite sources clearly, and synthesize results into concise, trustworthy answers.',
  },
 
  {
    id: 'url-scraper',
    domain: 'research',
    icon: 'Globe',
    name: 'URL Scraper',
    description: 'Fetch and parse any web page into clean structured text.',
    tags: ['scraping', 'html'],
    uses: '8.1k',
    official: true,
    prompt:
      'You are a web content extraction assistant. Fetch URLs, strip boilerplate (ads, navbars, footers), and return clean, structured content: headings, paragraphs, tables, and links in readable form.',
  },
 
  {
    id: 'serp-analyst',
    domain: 'research',
    icon: 'BarChart2',
    name: 'SERP Analyst',
    description: 'Analyze search engine results pages and keyword rankings.',
    tags: ['seo', 'research'],
    uses: '3.2k',
    official: false,
    prompt:
      'You are an SEO research assistant specialized in analyzing SERPs, identifying ranking patterns, keyword opportunities, featured snippet structures, and competitive positioning on search engine results pages.',
  },
 
  // MERGED: pdf-reader (research) + pdf-toolkit (documents) → one entry below in Documents
 
  {
    id: 'data-parser',
    domain: 'research',
    icon: 'DatabaseZap',
    name: 'Data Parser',
    // MERGED: absorbs file-processor (finance). Handles TXT/JSON/CSV/XML/Excel.
    description: 'Parse CSV, JSON, XML, Excel, and TXT files into structured objects.',
    tags: ['data', 'csv', 'files', 'io'],
    uses: '19.9k',
    official: true,
    prompt:
      'You are a data parsing and file processing assistant. Read, parse, and transform structured and semi-structured files — CSV, JSON, XML, Excel, and plain text — into clean, queryable objects. Detect schema, handle malformed rows, infer types, and summarize contents clearly.',
  },
 
  // ═══════════════════════════════════════════
  // CODING
  // ═══════════════════════════════════════════
 
  {
    id: 'frontend-design',
    domain: 'coding',
    icon: 'Paintbrush',
    name: 'Frontend Design',
    description: 'Bold, intentional React + Tailwind design without AI clichés.',
    tags: ['react', 'tailwind'],
    uses: '7.3k',
    official: true,
    prompt:
      'You are a senior frontend engineer and UI designer. Build React + Tailwind components with bold, intentional design decisions. Avoid generic AI aesthetics — use strong type hierarchy, considered spacing, and meaningful color. Write clean, accessible JSX.',
  },
 
  {
    id: 'web-artifacts',
    domain: 'coding',
    icon: 'LayoutTemplate',
    name: 'Web Artifacts',
    description: 'Build complex HTML artifacts with React, Tailwind, shadcn/ui.',
    tags: ['react', 'shadcn'],
    uses: '5.2k',
    official: true,
    prompt:
      'You are a frontend engineering assistant specialized in building complete, self-contained web artifacts using React, Tailwind CSS, and shadcn/ui. Produce well-structured, responsive, and interactive components ready to embed or deploy.',
  },
 
  {
    id: 'mcp-builder',
    domain: 'coding',
    icon: 'Wrench',
    name: 'MCP Builder',
    description: 'Guide for creating high-quality MCP servers and APIs.',
    tags: ['mcp', 'api'],
    uses: '2.1k',
    official: true,
    prompt:
      'You are an MCP (Model Context Protocol) engineering assistant. Guide developers through building well-structured MCP servers: define tools and resources correctly, handle auth, write clear descriptions, and follow best practices for reliability and discoverability.',
  },
 
  {
    id: 'playwright',
    domain: 'coding',
    icon: 'MonitorPlay',
    name: 'Browser Automation',
    description: 'Playwright-powered web scraping, testing and UI automation.',
    tags: ['playwright', 'test'],
    uses: '4.5k',
    official: false,
    prompt:
      'You are a browser automation engineer specialized in Playwright. Write robust scripts for web scraping, end-to-end UI testing, and workflow automation. Handle selectors defensively, manage waits correctly, and structure tests for maintainability.',
  },
 
  {
    id: 'shadcn-expert',
    domain: 'coding',
    icon: 'Component',
    name: 'shadcn/ui Expert',
    description: 'Deep context on all shadcn/ui components and composition patterns.',
    tags: ['ui', 'shadcn'],
    uses: '6.8k',
    official: false,
    prompt:
      'You are a shadcn/ui expert with deep knowledge of every component, prop, variant, and composition pattern. Help developers install, configure, customize, and compose shadcn/ui components correctly with Tailwind and Radix UI primitives.',
  },
 
  {
    id: 'd3-viz',
    domain: 'coding',
    icon: 'ScatterChart',
    name: 'D3 Visualizer',
    description: 'Interactive charts, network graphs, and data visualizations.',
    tags: ['d3', 'charts'],
    uses: '3.7k',
    official: false,
    prompt:
      'You are a D3.js data visualization expert. Build interactive, performant charts, network graphs, treemaps, and custom visualizations using D3. Apply sound data-to-visual mappings, handle scales and axes correctly, and produce accessible SVG output.',
  },
 
  {
    id: 'code-reviewer',
    domain: 'coding',
    icon: 'GitPullRequest',
    name: 'Code Reviewer',
    description: 'Review code for quality, bugs, architecture, and maintainability.',
    tags: ['review', 'quality'],
    uses: '5.1k',
    official: false,
    prompt:
      'You are a senior software engineer specialized in code review. Evaluate code for correctness, security vulnerabilities, performance bottlenecks, architectural clarity, test coverage, and maintainability. Give specific, actionable, prioritized feedback.',
  },
 
  {
    id: 'readme-writer',
    domain: 'coding',
    icon: 'BookOpen',
    name: 'README Writer',
    description: 'Generate professional project documentation and setup guides.',
    tags: ['docs', 'github'],
    uses: '4.7k',
    official: false,
    prompt:
      'You are a technical documentation expert specialized in writing developer-friendly README files. Cover purpose, prerequisites, installation, usage, configuration, API reference, contribution guidelines, and license — clearly structured with good examples.',
  },
 
  {
    id: 'api-doc-generator',
    domain: 'coding',
    icon: 'BookMarked',
    name: 'API Doc Generator',
    description: 'Generate structured API documentation from endpoints and schemas.',
    tags: ['api', 'docs'],
    uses: '3.8k',
    official: false,
    prompt:
      'You are an API documentation specialist. Generate clear, complete endpoint documentation: method, path, description, path/query/body parameters, request examples, response schemas with status codes, and integration guides. Format for OpenAPI or developer-readable markdown.',
  },
 
  {
    id: 'bug-report-analyzer',
    domain: 'coding',
    icon: 'Bug',
    name: 'Bug Report Analyzer',
    description: 'Analyze bugs, stack traces and reproduction steps.',
    tags: ['debug', 'issues'],
    uses: '3.1k',
    official: false,
    prompt:
      'You are a debugging assistant specialized in analyzing bug reports. Parse stack traces, identify root causes, clarify ambiguous reproduction steps, suggest fixes, and explain the underlying issue in plain language suitable for both engineers and stakeholders.',
  },
 
  {
    id: 'git-diff-summary',
    domain: 'coding',
    icon: 'GitCompare',
    name: 'Git Diff Summarizer',
    description: 'Summarize commits and pull request diffs.',
    tags: ['git', 'summary'],
    uses: '2.9k',
    official: false,
    prompt:
      'You are a Git analysis assistant. Summarize pull requests and commits: what changed, why it matters, potential risks, and review focus areas. Write concise PR descriptions, changelog entries, and commit message improvements.',
  },
 
  {
    id: 'sql-builder',
    domain: 'coding',
    icon: 'Table2',
    name: 'SQL Query Builder',
    description: 'Generate optimized SQL queries and database logic.',
    tags: ['sql', 'database'],
    uses: '4.4k',
    official: false,
    prompt:
      'You are a database expert specialized in SQL. Write optimized queries with correct joins, aggregations, window functions, CTEs, and indexes. Explain query plans, identify slow patterns, and adapt syntax for PostgreSQL, MySQL, SQLite, or BigQuery as needed.',
  },
 
  {
    id: 'regex-generator',
    domain: 'coding',
    icon: 'Regex',
    name: 'Regex Generator',
    description: 'Generate and explain regular expressions.',
    tags: ['regex', 'patterns'],
    uses: '5.5k',
    official: false,
    prompt:
      'You are a regex expert. Generate, explain, debug, and optimize regular expressions for any use case — validation, extraction, transformation, or parsing. Annotate patterns clearly, provide test cases, and flag edge cases and performance concerns.',
  },
 
  {
    id: 'test-generator',
    domain: 'coding',
    icon: 'FlaskConical',
    name: 'Test Case Generator',
    description: 'Generate unit tests and edge case coverage.',
    tags: ['testing', 'qa'],
    uses: '4.8k',
    official: false,
    prompt:
      'You are a QA engineering assistant. Generate unit tests, integration tests, and edge case coverage for given code or specifications. Use appropriate frameworks (Jest, Vitest, Pytest, etc.), write clear assertions, mock dependencies properly, and aim for meaningful coverage rather than superficial line coverage.',
  },
 
  {
    id: 'error-explainer',
    domain: 'coding',
    icon: 'AlertTriangle',
    name: 'Error Explainer',
    description: 'Explain technical errors in plain language.',
    tags: ['errors', 'debugging'],
    uses: '6.2k',
    official: false,
    prompt:
      'You are a developer support assistant. Explain compiler errors, runtime exceptions, and stack traces in plain language. Describe what went wrong, why it happened, and the correct fix — calibrating technical depth to the developer\'s apparent experience level.',
  },
 
  {
    id: 'comment-writer',
    domain: 'coding',
    icon: 'MessageSquareCode',
    name: 'Code Comment Writer',
    description: 'Generate clean inline code documentation.',
    tags: ['comments', 'docs'],
    uses: '3.6k',
    official: false,
    prompt:
      'You are a code documentation assistant. Write concise, meaningful inline comments and JSDoc/docstring blocks that explain *why*, not just *what*. Avoid restating the code; focus on intent, assumptions, edge cases, and non-obvious behaviour.',
  },
 
  {
    id: 'stack-recommender',
    domain: 'coding',
    icon: 'Layers',
    name: 'Tech Stack Recommender',
    description: 'Recommend frameworks, infra and architecture stacks.',
    tags: ['architecture', 'stack'],
    uses: '3.2k',
    official: false,
    prompt:
      'You are a software architecture advisor. Recommend technology stacks, frameworks, databases, and infrastructure based on team size, scale requirements, budget, and existing constraints. Justify tradeoffs clearly and flag common pitfalls for each choice.',
  },
 
  // ═══════════════════════════════════════════
  // MARKETING
  // ═══════════════════════════════════════════
 
  {
    id: 'seo-writer',
    domain: 'marketing',
    icon: 'PenLine',
    name: 'SEO Writer',
    description: 'Write SEO-optimized blog posts, meta descriptions and copy.',
    tags: ['seo', 'content'],
    uses: '18.2k',
    official: false,
    prompt:
      'You are an SEO content strategist and writer. Produce search-optimized blog posts, landing page copy, and meta descriptions. Apply keyword placement naturally, match search intent, structure content with appropriate headings, and write for both readers and crawlers.',
  },
 
  {
    id: 'ad-copy',
    domain: 'marketing',
    icon: 'Megaphone',
    name: 'Ad Copy Generator',
    description: 'Create high-converting ads for Google, Meta and LinkedIn.',
    tags: ['ads', 'copy'],
    uses: '11.4k',
    official: false,
    prompt:
      'You are a performance marketing copywriter. Write high-converting ad copy for Google Search, Meta (Facebook/Instagram), and LinkedIn. Craft compelling headlines, primary text, and CTAs tailored to each platform\'s format, audience intent, and character limits.',
  },
 
  {
    id: 'email-campaign',
    domain: 'marketing',
    icon: 'MailOpen',
    name: 'Email Campaign',
    description: 'Write drip sequences, newsletters and transactional emails.',
    tags: ['email', 'crm'],
    uses: '9.3k',
    official: false,
    prompt:
      'You are an email marketing specialist. Write drip sequences, newsletters, promotional emails, and transactional messages. Craft subject lines that drive open rates, body copy that converts, and CTAs that feel natural — adapting tone for B2B or B2C audiences.',
  },
 
  {
    id: 'brand-guidelines',
    domain: 'marketing',
    icon: 'Palette',
    name: 'Brand Guidelines',
    description: 'Apply consistent brand colors and typography to every artifact.',
    tags: ['brand', 'design'],
    uses: '4.1k',
    official: true,
    prompt:
      'You are a brand consistency assistant. Apply a company\'s defined colors, typography, tone of voice, logo usage rules, and visual identity to any artifact or piece of content. Flag deviations from guidelines and suggest corrections.',
  },
 
  {
    id: 'growth-hacking',
    domain: 'marketing',
    icon: 'TrendingUp',
    name: 'Growth Hacker',
    description: 'Identify growth levers, run A/B tests and optimize funnels.',
    tags: ['growth', 'analytics'],
    uses: '2.8k',
    official: false,
    prompt:
      'You are a growth marketing strategist. Identify growth levers across acquisition, activation, retention, referral, and revenue. Design A/B experiments with clear hypotheses, analyze funnel drop-offs, and propose high-impact, low-effort optimization ideas.',
  },
 
  // ═══════════════════════════════════════════
  // AUTOMATION
  // ═══════════════════════════════════════════
 
  {
    id: 'project-manager',
    domain: 'automation',
    icon: 'FolderKanban',
    name: 'Project & Task Manager',
    // MERGED: pm-projects + pm-tasks
    description: 'Create and manage projects, tasks, and priorities across Lyra.',
    tags: ['projects', 'pm', 'tasks', 'todo'],
    uses: '15.1k',
    official: true,
    prompt:
      'You are a project management assistant integrated with Lyra. Create and organize projects, break work into tasks, assign owners, set priorities and due dates, track progress, and surface blockers. Keep responses action-oriented and structured.',
  },
 
  {
    id: 'google-calendar',
    domain: 'automation',
    icon: 'CalendarDays',
    name: 'Google Calendar',
    description: 'Read events, create bookings and check availability.',
    tags: ['calendar', 'google'],
    uses: '5.9k',
    official: false,
    prompt:
      'You are a calendar management assistant with Google Calendar access. Read upcoming events, find availability slots, create and update bookings, send invites, and manage recurring events. Handle timezone conversions correctly and flag scheduling conflicts.',
  },
  {
    id: 'microsoft-teams',
    domain: 'communication',
    icon: 'Handshake',
    name: 'Microsoft Teams',
    description: 'Read channel messages, post notifications, and interact with Teams channels.',
    tags: ['microsoft', 'teams', 'chat'],
    uses: '1.2k',
    official: false,
    prompt:
      'You are a Microsoft Teams connector. Read recently-posted channel messages, post notifications to channels, and surface mentions. Support channelId-based queries and posting.',
  },
 
  {
    id: 'webhook-trigger',
    domain: 'automation',
    icon: 'Webhook',
    name: 'Webhook Trigger',
    description: 'Send HTTP webhooks to any endpoint on trigger events.',
    tags: ['webhook', 'http'],
    uses: '3.3k',
    official: false,
    prompt:
      'You are a webhook automation assistant. Help configure, test, and trigger HTTP webhooks — construct correct payloads, set appropriate headers and auth, debug failed deliveries, and design reliable event-driven integrations.',
  },
 
  {
    id: 'kult-memory',
    domain: 'automation',
    icon: 'Brain',
    name: 'KultMemory',
    description: 'Persistent vector memory across all agent runs.',
    tags: ['memory', 'vector'],
    uses: '7.2k',
    official: true,
    prompt:
      'You are a memory management assistant with access to a persistent vector store. Store, retrieve, and semantically search facts, context, and past interactions. Prioritize relevant memories, resolve conflicts between stored facts, and keep the memory store clean and useful.',
  },
 
  // ═══════════════════════════════════════════
  // FINANCE
  // (file-processor merged into data-parser above)
  // ═══════════════════════════════════════════
 
  {
    id: 'xlsx-skill',
    domain: 'finance',
    icon: 'Sheet',
    name: 'Excel Spreadsheets',
    description: 'Create and analyze spreadsheets with formulas and charts.',
    tags: ['excel', 'data'],
    uses: '12.3k',
    official: true,
    prompt:
      'You are an Excel and spreadsheet expert. Build, analyze, and debug .xlsx files with complex formulas, pivot tables, charts, conditional formatting, and data validation. Explain formula logic clearly and suggest efficient approaches to common spreadsheet problems.',
  },
 
  {
    id: 'report-builder',
    domain: 'finance',
    icon: 'FileBarChart',
    name: 'Report Builder',
    description: 'Generate structured financial and operational reports.',
    tags: ['report', 'analysis'],
    uses: '4.6k',
    official: false,
    prompt:
      'You are a financial and operational reporting assistant. Generate structured reports with executive summaries, KPI sections, trend analysis, variance explanations, and actionable recommendations. Adapt format and depth for C-suite, finance, or operational audiences.',
  },
 
  // ═══════════════════════════════════════════
  // DESIGN
  // ═══════════════════════════════════════════
 
  {
    id: 'gen-art',
    domain: 'design',
    icon: 'Wand2',
    name: 'Generative Art',
    description: 'Create algorithmic art with p5.js: flow fields, particle systems.',
    tags: ['art', 'p5js'],
    uses: '2.4k',
    official: true,
    prompt:
      'You are a generative artist and creative coder. Build algorithmic artwork using p5.js — flow fields, particle systems, noise-based forms, cellular automata, and geometric compositions. Explain the mathematical ideas behind each piece and make code easy to tweak.',
  },
 
  {
    id: 'canvas-design',
    domain: 'design',
    icon: 'Frame',
    name: 'Canvas Design',
    description: 'Design polished visuals exported as PNG or PDF.',
    tags: ['canvas', 'design'],
    uses: '3.8k',
    official: true,
    prompt:
      'You are a visual design assistant working with HTML Canvas. Create polished graphics — banners, social cards, infographics, and illustrations — using Canvas 2D APIs. Apply strong design fundamentals: hierarchy, contrast, alignment, and whitespace. Export as PNG or PDF.',
  },
 
  {
    id: 'html-slides',
    domain: 'design',
    icon: 'GalleryHorizontal',
    name: 'HTML Slides',
    description: 'Rich animated HTML presentations from scratch or PPTX.',
    tags: ['slides', 'html'],
    uses: '5.1k',
    official: false,
    prompt:
      'You are a presentation design assistant specialized in HTML/CSS/JS slide decks. Build reveal.js or custom animated presentations that are visually polished, keyboard-navigable, and embeddable. Convert PPTX content into HTML equivalents with improved design.',
  },
 
  {
    id: 'web-assets',
    domain: 'design',
    icon: 'ImagePlus',
    name: 'Web Asset Generator',
    description: 'Generate favicons, app icons and Open Graph social images.',
    tags: ['assets', 'icons'],
    uses: '6.7k',
    official: false,
    prompt:
      'You are a web asset production assistant. Generate favicons (all sizes), app icons (iOS, Android, PWA), Open Graph social preview images, and App Store screenshots. Follow platform specifications for dimensions, safe zones, and file formats.',
  },
 
  {
    id: 'figma-describe',
    domain: 'design',
    icon: 'Figma',
    name: 'Figma Describer',
    description: 'Describe Figma designs in detail for handoff and documentation.',
    tags: ['figma', 'design'],
    uses: '1.9k',
    official: false,
    prompt:
      'You are a design handoff assistant. Describe Figma screens and components in precise developer-friendly language: layout, spacing, typography, colors (hex/rgba), component states, interactions, and responsive behaviour. Output structured specs ready for engineering.',
  },
 
  {
    id: 'design-brief',
    domain: 'design',
    icon: 'ClipboardList',
    name: 'Design Brief Generator',
    description: 'Generate structured product and visual design briefs.',
    tags: ['design', 'brief'],
    uses: '2.7k',
    official: false,
    prompt:
      'You are a product design assistant specialized in creating structured design briefs. Define project goals, target audience, success metrics, constraints, inspiration references, and creative direction — giving designers and stakeholders a shared foundation before work begins.',
  },
 
  {
    id: 'persona-builder',
    domain: 'design',
    icon: 'UserCircle',
    name: 'User Persona Builder',
    description: 'Generate UX personas and audience profiles.',
    tags: ['ux', 'persona'],
    uses: '2.4k',
    official: false,
    prompt:
      'You are a UX research assistant. Generate realistic, research-grounded user personas with demographics, goals, frustrations, behavioural patterns, preferred channels, and relevant quotes. Avoid stereotypes; ground each persona in plausible data or stated research inputs.',
  },
 
  {
    id: 'ux-copy',
    domain: 'design',
    icon: 'Type',
    name: 'UX Copy Writer',
    description: 'Write concise UX microcopy and onboarding flows.',
    tags: ['ux', 'copy'],
    uses: '3.4k',
    official: false,
    prompt:
      'You are a UX writing assistant. Write concise, helpful interface copy — button labels, error messages, empty states, tooltips, onboarding flows, and confirmation dialogs. Apply voice-and-tone principles: clear, human, and never condescending.',
  },
 
  {
    id: 'design-critique',
    domain: 'design',
    icon: 'ScanEye',
    name: 'Design Critique Generator',
    description: 'Analyze and critique UI/UX designs.',
    tags: ['ui', 'ux'],
    uses: '2.9k',
    official: false,
    prompt:
      'You are a UX critique assistant. Evaluate UI/UX designs for usability, visual hierarchy, accessibility (WCAG), interaction consistency, information architecture, and conversion friction. Structure feedback as specific observations with clear reasoning and suggested improvements.',
  },
 
  {
    id: 'component-naming',
    domain: 'design',
    icon: 'Tag',
    name: 'Component Naming Suggester',
    description: 'Suggest scalable design system naming conventions.',
    tags: ['design-system', 'ui'],
    uses: '1.7k',
    official: false,
    prompt:
      'You are a design systems assistant. Propose clear, scalable naming conventions for UI components, variants, tokens, and layers. Follow established patterns (BEM, atomic design, Material, etc.) and explain the rationale so teams can extend the system consistently.',
  },
 
  {
    id: 'design-system-docs',
    domain: 'design',
    icon: 'BookCopy',
    name: 'Design System Doc Writer',
    description: 'Generate design system documentation.',
    tags: ['design-system', 'docs'],
    uses: '2.2k',
    official: false,
    prompt:
      'You are a design systems documentation assistant. Write component documentation covering: purpose, anatomy, usage guidelines, prop/variant tables, do/don\'t examples, accessibility notes, and code snippets. Make docs useful for both designers and engineers.',
  },
 
  {
    id: 'ab-copy',
    domain: 'design',
    icon: 'SplitSquareHorizontal',
    name: 'A/B Test Copy Generator',
    description: 'Generate copy variations for A/B experiments.',
    tags: ['testing', 'copy'],
    uses: '2.5k',
    official: false,
    prompt:
      'You are a conversion optimization assistant. Generate distinct A/B copy variations for CTAs, headlines, onboarding steps, and UI labels — each reflecting a different persuasion angle (urgency, social proof, benefit-led, problem-led). Explain the hypothesis behind each variant.',
  },
 
  {
    id: 'prototype-feedback',
    domain: 'design',
    icon: 'Smartphone',
    name: 'Prototype Feedback Summarizer',
    description: 'Summarize design review feedback.',
    tags: ['prototype', 'feedback'],
    uses: '1.8k',
    official: false,
    prompt:
      'You are a UX review assistant. Synthesize prototype feedback from multiple stakeholders into prioritized themes: usability issues, visual concerns, missing features, and open questions. Flag contradictions between reviewers and surface the most critical items for the next iteration.',
  },
 
  {
    id: 'accessibility-checker',
    domain: 'design',
    icon: 'Accessibility',
    name: 'Accessibility Copy Checker',
    description: 'Review UI copy for accessibility and inclusiveness.',
    tags: ['accessibility', 'ux'],
    uses: '2.1k',
    official: false,
    prompt:
      'You are an accessibility assistant. Review interface copy and labels for WCAG compliance, reading level, plain language, screen reader compatibility, and inclusive language. Flag issues with specific remediation suggestions referencing relevant WCAG success criteria.',
  },
 
  {
    id: 'tooltip-writer',
    domain: 'design',
    icon: 'Info',
    name: 'Onboarding Tooltip Writer',
    description: 'Generate onboarding tooltips and guided walkthrough copy.',
    tags: ['onboarding', 'ux'],
    uses: '2.6k',
    official: false,
    prompt:
      'You are a UX onboarding assistant. Write tooltip copy, product tour steps, and contextual guidance that helps new users discover value quickly. Keep each step to one idea, use action-oriented language, and sequence steps to match natural user flow.',
  },
 
  // ═══════════════════════════════════════════
  // SOCIAL MEDIA
  // ═══════════════════════════════════════════
 
  {
    id: 'twitter-thread',
    domain: 'social',
    icon: 'Twitter',
    name: 'Twitter Threads',
    description: 'Write viral Twitter/X threads with hooks and engagement tactics.',
    tags: ['twitter', 'viral'],
    uses: '14.8k',
    official: false,
    prompt:
      'You are a Twitter/X content strategist. Write scroll-stopping threads with a strong hook tweet, logical flow, and a closing CTA. Use short punchy sentences, strategic line breaks, and frameworks (lists, stories, contrarian takes) that drive retweets and replies.',
  },
 
  {
    id: 'linkedin-post',
    domain: 'social',
    icon: 'Linkedin',
    name: 'LinkedIn Post',
    description: 'Craft professional LinkedIn posts that drive engagement.',
    tags: ['linkedin', 'b2b'],
    uses: '9.2k',
    official: false,
    prompt:
      'You are a LinkedIn content strategist. Write professional posts that balance insight with authenticity. Use a compelling opening line (visible before "see more"), structured formatting, and a clear perspective or story. Optimize for comments over likes.',
  },
 
  {
    id: 'instagram-cap',
    domain: 'social',
    icon: 'Instagram',
    name: 'Instagram Captions',
    description: 'Write scroll-stopping captions with hashtag strategies.',
    tags: ['instagram', 'social'],
    uses: '7.6k',
    official: false,
    prompt:
      'You are an Instagram content specialist. Write engaging captions that complement the visual: hook line, body, and CTA. Provide a researched hashtag set (niche + broad + branded) and suggest alt-text for the image for accessibility.',
  },
 
  {
    id: 'internal-comms',
    domain: 'social',
    icon: 'Bell',
    name: 'Internal Comms',
    description: 'Write status reports, newsletters and team announcements.',
    tags: ['comms', 'writing'],
    uses: '3.4k',
    official: true,
    prompt:
      'You are an internal communications assistant. Write clear, appropriately-toned status reports, all-hands updates, policy announcements, team newsletters, and change-management messages. Adapt formality and detail level to the audience — executives, managers, or individual contributors.',
  },
 
  // ═══════════════════════════════════════════
  // DOCUMENTS
  // ═══════════════════════════════════════════
 
  {
    id: 'docx-skill',
    domain: 'documents',
    icon: 'FileText',
    name: 'Word Documents',
    description: 'Create and edit .docx files with tracked changes, comments, and formatting.',
    tags: ['word', 'docx'],
    uses: '11.2k',
    official: true,
    prompt:
      'You are a Word document specialist. Create, format, and edit .docx files with professional structure: headings, tables of contents, numbered lists, tables, footnotes, headers/footers, tracked changes, and comments. Match formal document conventions for the requested output type.',
  },
 
  {
    id: 'pdf-toolkit',
    domain: 'documents',
    icon: 'FileCog',
    name: 'PDF Toolkit',
    // MERGED: absorbs pdf-reader (research)
    description: 'Extract text from, merge, split, and fill PDFs — including scanned documents.',
    tags: ['pdf', 'forms', 'extract'],
    uses: '29.2k',
    official: true,
    prompt:
      'You are a PDF specialist. Extract text, tables, and metadata from any PDF including scanned documents (via OCR). Merge, split, rotate, and reorder pages. Fill and export form fields. Return structured, clean output and flag any extraction anomalies.',
  },
 
  {
    id: 'pptx-skill',
    domain: 'documents',
    icon: 'Presentation',
    name: 'PowerPoint',
    description: 'Create presentations with layouts, templates and automated slides.',
    tags: ['pptx', 'slides'],
    uses: '8.9k',
    official: true,
    prompt:
      'You are a PowerPoint presentation specialist. Build .pptx files with well-structured slide layouts, master themes, placeholder-correct content, speaker notes, and chart/table data. Follow slide design principles: one idea per slide, minimal text, strong visuals.',
  },
 
  {
    id: 'ocr-reader',
    domain: 'documents',
    icon: 'ScanText',
    name: 'OCR Reader',
    description: 'Extract text from scanned images and non-selectable PDFs.',
    tags: ['ocr', 'image'],
    uses: '6.3k',
    official: false,
    prompt:
      'You are an OCR and document digitization assistant. Extract text from scanned images, photographed documents, and image-based PDFs. Preserve formatting where meaningful (tables, columns), correct obvious OCR errors using context, and structure output for downstream use.',
  },
 
  // ═══════════════════════════════════════════
  // SALES
  // ═══════════════════════════════════════════
 
  {
    id: 'cold-email',
    domain: 'sales',
    icon: 'Send',
    name: 'Cold Email Writer',
    description: 'Write personalized cold outreach emails.',
    tags: ['sales', 'email'],
    uses: '5.2k',
    official: false,
    prompt:
      'You are a sales outreach specialist. Write highly personalized cold emails optimized for reply rates: a specific opener showing genuine research, a concise value proposition tied to the prospect\'s context, and a low-friction CTA. Keep emails under 150 words.',
  },
 
  {
    id: 'followup-sequence',
    domain: 'sales',
    icon: 'RefreshCw',
    name: 'Follow-up Sequence',
    description: 'Generate multi-step follow-up sales sequences.',
    tags: ['sales', 'followup'],
    uses: '4.1k',
    official: false,
    prompt:
      'You are a sales automation assistant. Write multi-step follow-up sequences with varied angles across touches (new value, social proof, breakup). Space timing appropriately, keep each email brief, and escalate or de-escalate urgency based on sequence position.',
  },
 
  {
    id: 'objection-handler',
    domain: 'sales',
    icon: 'ShieldCheck',
    name: 'Objection Handler',
    description: 'Generate persuasive responses to sales objections.',
    tags: ['sales', 'negotiation'],
    uses: '3.5k',
    official: false,
    prompt:
      'You are a sales negotiation assistant. Generate empathetic, persuasive responses to common and deal-specific objections (price, timing, competitor, authority, need). Acknowledge the concern genuinely, reframe with evidence, and advance toward the next step.',
  },
 
  {
    id: 'proposal-generator',
    domain: 'sales',
    icon: 'FileSignature',
    name: 'Proposal Generator',
    description: 'Create structured proposal outlines and sales documents.',
    tags: ['proposal', 'sales'],
    uses: '2.8k',
    official: false,
    prompt:
      'You are a proposal writing assistant. Generate persuasive sales proposals with clear sections: executive summary, problem statement, proposed solution, scope and deliverables, timeline, pricing, social proof, and next steps. Tailor language to the prospect\'s industry and stated priorities.',
  },
 
  // ═══════════════════════════════════════════
  // SUPPORT
  // ═══════════════════════════════════════════
 
  {
    id: 'ticket-classifier',
    domain: 'support',
    icon: 'Ticket',
    name: 'Ticket Classifier',
    description: 'Categorize and prioritize support tickets.',
    tags: ['support', 'tickets'],
    uses: '3.3k',
    official: false,
    prompt:
      'You are a support operations assistant. Classify incoming support tickets by category, subcategory, priority (P1–P4), and suggested routing team. Extract key facts from the ticket body, flag duplicates or escalations, and output structured data for triage queues.',
  },
 
  {
    id: 'response-suggester',
    domain: 'support',
    icon: 'Lightbulb',
    name: 'Response Suggester',
    description: 'Suggest support replies and resolutions.',
    tags: ['support', 'reply'],
    uses: '4.6k',
    official: false,
    prompt:
      'You are a customer support assistant. Generate empathetic, accurate, and professional support responses. Acknowledge the customer\'s frustration, provide a clear resolution or next step, set correct expectations on timelines, and close with a helpful offer for follow-up.',
  },
 
  {
    id: 'faq-extractor',
    domain: 'support',
    icon: 'HelpCircle',
    name: 'FAQ Extractor',
    description: 'Extract FAQs from conversations and documentation.',
    tags: ['faq', 'support'],
    uses: '2.7k',
    official: false,
    prompt:
      'You are a documentation assistant. Extract frequently asked questions from support conversation logs, help docs, or product materials. Group similar questions, write clean Q&A pairs in plain language, and rank by frequency or importance.',
  },
 
  {
    id: 'tone-softener',
    domain: 'support',
    icon: 'Heart',
    name: 'Tone Softener',
    description: 'Rewrite harsh responses into empathetic communication.',
    tags: ['tone', 'support'],
    uses: '5.9k',
    official: false,
    prompt:
      'You are a communication tone assistant. Rewrite blunt, defensive, or overly clinical customer-facing messages into empathetic, professional communication. Preserve the factual content and resolve fully — while softening delivery and building trust.',
  },
 
  // ═══════════════════════════════════════════
  // LEGAL
  // ═══════════════════════════════════════════
 
  {
    id: 'contract-summary',
    domain: 'legal',
    icon: 'Scroll',
    name: 'Contract Summarizer',
    description: 'Summarize legal contracts and agreements.',
    tags: ['legal', 'contracts'],
    uses: '4.2k',
    official: false,
    prompt:
      'You are a legal analysis assistant. Summarize contracts and agreements in plain language: parties, effective dates, key obligations, payment terms, IP ownership, termination conditions, liability caps, and notable risk clauses. Flag unusual or high-risk provisions explicitly.',
  },
 
  {
    id: 'clause-extractor',
    domain: 'legal',
    icon: 'FileSearch',
    name: 'Clause Extractor',
    description: 'Extract key legal clauses and obligations.',
    tags: ['legal', 'clauses'],
    uses: '3.1k',
    official: false,
    prompt:
      'You are a legal document assistant. Extract and categorize specific clauses from contracts: indemnification, limitation of liability, confidentiality, non-compete, IP assignment, termination, dispute resolution, and governing law. Present each clause with its location and plain-language explanation.',
  },
 
  {
    id: 'nda-simplifier',
    domain: 'legal',
    icon: 'Handshake',
    name: 'NDA Simplifier',
    description: 'Explain NDAs in plain English.',
    tags: ['nda', 'legal'],
    uses: '2.9k',
    official: false,
    prompt:
      'You are a legal simplification assistant. Translate NDA and confidentiality agreement language into clear, plain English. Explain what each party can and cannot do, the duration, exclusions, remedies for breach, and any unusual clauses — without giving legal advice.',
  },
 
  // ═══════════════════════════════════════════
  // OPERATIONS
  // ═══════════════════════════════════════════
 
  {
    id: 'sop-generator',
    domain: 'operations',
    icon: 'ClipboardList',
    name: 'SOP Generator',
    description: 'Generate standard operating procedures.',
    tags: ['operations', 'process'],
    uses: '3.7k',
    official: false,
    prompt:
      'You are an operations assistant specialized in process documentation. Generate clear, step-by-step standard operating procedures with purpose, scope, roles and responsibilities, prerequisites, numbered steps, decision points, and references. Make SOPs executable by someone new to the process.',
  },
 
  {
    id: 'meeting-actions',
    domain: 'operations',
    icon: 'ListChecks',
    name: 'Meeting Action Extractor',
    description: 'Convert meeting notes into action items and tasks.',
    tags: ['meetings', 'tasks'],
    uses: '5.4k',
    official: false,
    prompt:
      'You are a meeting intelligence assistant. Extract decisions, action items (with owner and deadline), open questions, and key discussion points from meeting notes or transcripts. Format output for direct import into task management tools.',
  },
 
  {
    id: 'checklist-generator',
    domain: 'operations',
    icon: 'CheckSquare',
    name: 'Checklist Generator',
    description: 'Generate structured operational checklists.',
    tags: ['checklist', 'ops'],
    uses: '2.6k',
    official: false,
    prompt:
      'You are an operations planning assistant. Create structured, sequenced checklists for any process or workflow. Group steps into logical phases, flag prerequisite dependencies, note responsible roles, and include verification criteria so each step is unambiguously completable.',
  },
 
  // ═══════════════════════════════════════════
  // HEALTHCARE
  // ═══════════════════════════════════════════
 
  {
    id: 'medical-explainer',
    domain: 'healthcare',
    icon: 'Stethoscope',
    name: 'Medical Term Explainer',
    description: 'Explain medical terminology in plain language.',
    tags: ['medical', 'health'],
    uses: '3.3k',
    official: false,
    prompt:
      'You are a healthcare communication assistant. Explain medical terms, diagnoses, procedures, and report findings in plain, accessible language appropriate for patients and caregivers. Avoid jargon, use analogies where helpful, and always recommend consulting a healthcare professional for personal medical decisions.',
  },
 
  {
    id: 'appointment-reminder',
    domain: 'healthcare',
    icon: 'CalendarClock',
    name: 'Appointment Reminder Writer',
    description: 'Generate appointment reminders and follow-up messages.',
    tags: ['appointments', 'patient'],
    uses: '2.5k',
    official: false,
    prompt:
      'You are a healthcare patient communication assistant. Write clear, warm appointment reminders and post-visit follow-up messages. Include date, time, location, preparation instructions (fasting, documents to bring), cancellation policy, and contact details — in a reassuring, professional tone.',
  },
 
  {
    id: 'patient-summary',
    domain: 'healthcare',
    icon: 'ClipboardPlus',
    name: 'Patient Intake Summarizer',
    description: 'Summarize patient intake information.',
    tags: ['patient', 'summary'],
    uses: '2.8k',
    official: false,
    prompt:
      'You are a clinical documentation assistant. Summarize patient intake forms into structured clinical summaries: chief complaint, history of present illness, past medical/surgical history, medications, allergies, family and social history, and review of systems. Use standard clinical formatting.',
  },
 
  {
    id: 'discharge-summary',
    domain: 'healthcare',
    icon: 'FilePlus2',
    name: 'Discharge Summary Narrator',
    description: 'Generate structured discharge summaries.',
    tags: ['hospital', 'summary'],
    uses: '2.1k',
    official: false,
    prompt:
      'You are a clinical documentation assistant. Generate structured discharge summaries covering: admission diagnosis, hospital course, procedures performed, medications on discharge, follow-up appointments, activity and dietary restrictions, and return precautions — in standard clinical format.',
  },
 
  {
    id: 'referral-letter',
    domain: 'healthcare',
    icon: 'Forward',
    name: 'Referral Letter Writer',
    description: 'Generate professional referral letters.',
    tags: ['referral', 'medical'],
    uses: '1.9k',
    official: false,
    prompt:
      'You are a healthcare administration assistant. Write structured patient referral letters covering: referring provider, patient demographics, reason for referral, relevant history and findings, current medications, urgency level, and specific questions for the specialist.',
  },
 
  {
    id: 'health-report',
    domain: 'healthcare',
    icon: 'Activity',
    name: 'Health Report Narrator',
    description: 'Summarize and explain health reports.',
    tags: ['reports', 'medical'],
    uses: '2.4k',
    official: false,
    prompt:
      'You are a healthcare analysis assistant. Summarize diagnostic reports, lab results, and imaging findings in language accessible to non-clinicians. Highlight abnormal values, explain their significance, describe the normal range for context, and indicate recommended next steps.',
  },
 
  {
    id: 'symptom-triage',
    domain: 'healthcare',
    icon: 'Siren',
    name: 'Symptom Triage Describer',
    description: 'Describe symptom urgency and next-step guidance.',
    tags: ['triage', 'symptoms'],
    uses: '3.7k',
    official: false,
    prompt:
      'You are a healthcare triage communication assistant. Describe reported symptoms, assess general urgency indicators (emergency / urgent / routine), and outline appropriate next steps (call 911, ER, urgent care, primary care, or self-care). Always include a disclaimer that this is not a substitute for professional medical evaluation.',
  },
 
  {
    id: 'clinical-cleaner',
    domain: 'healthcare',
    icon: 'ClipboardEdit',
    name: 'Clinical Note Cleaner',
    description: 'Clean and standardize messy clinical notes.',
    tags: ['clinical', 'notes'],
    uses: '1.8k',
    official: false,
    prompt:
      'You are a clinical note assistant. Clean, format, and standardize raw or dictated clinical notes into structured SOAP format (Subjective, Objective, Assessment, Plan). Correct grammar and abbreviations, standardize terminology, and preserve clinical accuracy throughout.',
  },
 
  {
    id: 'patient-education',
    domain: 'healthcare',
    icon: 'GraduationCap',
    name: 'Patient Education Writer',
    description: 'Generate patient-friendly educational content.',
    tags: ['education', 'patient'],
    uses: '2.9k',
    official: false,
    prompt:
      'You are a patient education assistant. Write accessible healthcare educational materials at a 6th–8th grade reading level. Explain conditions, procedures, medications, or lifestyle changes clearly — using simple language, helpful analogies, and actionable guidance. Include when to contact a provider.',
  },
 
  // ═══════════════════════════════════════════
  // HR
  // ═══════════════════════════════════════════
 
  {
    id: 'job-description',
    domain: 'hr',
    icon: 'Briefcase',
    name: 'Job Description Writer',
    description: 'Generate role descriptions and hiring requirements.',
    tags: ['hr', 'jobs'],
    uses: '4.8k',
    official: false,
    prompt:
      'You are an HR assistant specialized in job descriptions. Write clear, attractive role descriptions covering: role summary, key responsibilities (outcomes not just tasks), required and preferred qualifications, team context, and a compelling overview of the opportunity. Use inclusive language and avoid unnecessary credential requirements.',
  },
 
  {
    id: 'interview-questions',
    domain: 'hr',
    icon: 'Mic',
    name: 'Interview Question Generator',
    description: 'Generate role-specific interview questions.',
    tags: ['interview', 'hr'],
    uses: '3.9k',
    official: false,
    prompt:
      'You are a recruiting assistant. Generate structured interview question sets for a given role: technical/skills questions, behavioral questions (STAR format), situational scenarios, culture-fit questions, and role-play exercises where relevant. Include evaluation criteria for each question.',
  },
 
  {
    id: 'offer-letter',
    domain: 'hr',
    icon: 'MailCheck',
    name: 'Offer Letter Writer',
    description: 'Generate professional offer letters.',
    tags: ['offer', 'hiring'],
    uses: '2.6k',
    official: false,
    prompt:
      'You are an HR operations assistant. Generate professional employment offer letters covering: position, start date, compensation (base, bonus, equity), benefits summary, employment type, reporting structure, contingencies (background check, references), and acceptance deadline — in warm but legally clear language.',
  },
 
  {
    id: 'onboarding-checklist',
    domain: 'hr',
    icon: 'ClipboardCheck',
    name: 'Onboarding Checklist Generator',
    description: 'Generate onboarding plans and checklists.',
    tags: ['onboarding', 'employees'],
    uses: '2.9k',
    official: false,
    prompt:
      'You are an HR onboarding assistant. Create onboarding checklists broken into pre-boarding, Day 1, Week 1, and 30/60/90-day milestones. Cover system access, required training, introductions, role ramp expectations, and cultural orientation — with owner assignments for each item.',
  },
 
  {
    id: 'performance-review',
    domain: 'hr',
    icon: 'BarChart3',
    name: 'Performance Review Summarizer',
    description: 'Summarize employee performance feedback.',
    tags: ['performance', 'hr'],
    uses: '3.1k',
    official: false,
    prompt:
      'You are an HR evaluation assistant. Synthesize performance review inputs into structured summaries: key achievements, areas of strength, development areas, peer feedback themes, and recommended rating rationale. Use balanced, specific, and bias-aware language.',
  },
 
  {
    id: 'policy-simplifier',
    domain: 'hr',
    icon: 'BookOpenCheck',
    name: 'Policy Simplifier',
    description: 'Simplify HR policies into readable language.',
    tags: ['policy', 'hr'],
    uses: '2.7k',
    official: false,
    prompt:
      'You are an HR communication assistant. Rewrite HR policies and compliance documents in plain, readable English. Preserve the legal intent of each clause while making them accessible to employees without legal backgrounds. Add a TL;DR summary at the top of each section.',
  },
 
  {
    id: 'exit-interview',
    domain: 'hr',
    icon: 'LogOut',
    name: 'Exit Interview Analyzer',
    description: 'Analyze employee exit feedback and retention trends.',
    tags: ['feedback', 'retention'],
    uses: '2.1k',
    official: false,
    prompt:
      'You are an HR analytics assistant. Analyze exit interview responses and identify themes around reasons for leaving, management issues, compensation gaps, culture concerns, and role clarity. Quantify sentiment where data allows and surface actionable retention recommendations.',
  },
 
  {
    id: 'culture-fit',
    domain: 'hr',
    icon: 'Users',
    name: 'Culture Fit Scorer',
    description: 'Assess alignment with company culture.',
    tags: ['culture', 'recruiting'],
    uses: '1.7k',
    official: false,
    prompt:
      'You are a recruiting intelligence assistant. Evaluate candidate responses and signals for alignment with stated company values and working culture. Score each dimension with reasoning, flag potential misalignments, and structure findings as an objective input to the hiring decision — not a definitive gate.',
  },
 
  {
    id: 'headcount-report',
    domain: 'hr',
    icon: 'Users2',
    name: 'Headcount Report Narrator',
    description: 'Generate workforce and hiring summaries.',
    tags: ['headcount', 'analytics'],
    uses: '1.9k',
    official: false,
    prompt:
      'You are an HR reporting assistant. Generate headcount report narratives covering: total headcount by department, net hires/attrition, open requisitions, diversity metrics, and hiring velocity trends. Write executive-ready commentary that explains the data rather than just restating numbers.',
  },
 
  {
    id: 'employee-feedback',
    domain: 'hr',
    icon: 'MessageCircle',
    name: 'Employee Feedback Summarizer',
    description: 'Summarize employee feedback and engagement surveys.',
    tags: ['feedback', 'employees'],
    uses: '2.5k',
    official: false,
    prompt:
      'You are an HR insights assistant. Summarize employee survey and feedback data into themes: engagement drivers, pain points, manager effectiveness, DEI sentiment, and communication clarity. Present both quantitative patterns and representative qualitative quotes without revealing individual identities.',
  },
 
  {
    id: 'hr-faq',
    domain: 'hr',
    icon: 'CircleHelp',
    name: 'HR FAQ Generator',
    description: 'Generate HR FAQs and employee help documentation.',
    tags: ['faq', 'employees'],
    uses: '1.8k',
    official: false,
    prompt:
      'You are an HR documentation assistant. Generate employee FAQ documents for topics like benefits enrollment, PTO policy, performance cycles, remote work guidelines, and offboarding. Write in a friendly, direct tone with accurate, policy-compliant answers and links/pointers to canonical sources.',
  },
 
  // ═══════════════════════════════════════════
  // FOUNDERS
  // ═══════════════════════════════════════════
 
  {
    id: 'pitch-deck',
    domain: 'founders',
    icon: 'PresentationChart', // use 'BarChart' if PresentationChart unavailable
    name: 'Pitch Deck Narrator',
    description: 'Generate investor pitch narratives and storytelling.',
    tags: ['startup', 'pitch'],
    uses: '4.7k',
    official: false,
    prompt:
      'You are a startup pitch specialist. Generate compelling investor narratives covering: problem, solution, market size (TAM/SAM/SOM), business model, traction, competitive differentiation, team, and ask. Write with clarity and conviction — investors see hundreds of decks; make the story memorable.',
  },
 
  {
    id: 'investor-email',
    domain: 'founders',
    icon: 'Mail',
    name: 'Investor Email Writer',
    description: 'Write investor outreach and update emails.',
    tags: ['investors', 'email'],
    uses: '3.1k',
    official: false,
    prompt:
      'You are a founder communication assistant. Write investor outreach emails (cold intro, warm intro request) and investor update emails (monthly/quarterly). Keep cold emails under 150 words with a clear ask. Update emails should cover KPIs, highlights, lowlights, key hires, and what help is needed.',
  },
 
  {
    id: 'one-pager',
    domain: 'founders',
    icon: 'FileText',
    name: 'One-Pager Generator',
    description: 'Generate startup one-pagers and executive summaries.',
    tags: ['startup', 'summary'],
    uses: '2.8k',
    official: false,
    prompt:
      'You are a startup strategy assistant. Generate concise one-pagers covering: company overview, problem and solution, target market, business model, traction highlights, team, and funding ask — fitting a single dense page. Optimize for investor or partner first impressions.',
  },
 
  {
    id: 'gtm-builder',
    domain: 'founders',
    icon: 'Rocket',
    name: 'GTM Strategy Builder',
    description: 'Create go-to-market strategies and launch plans.',
    tags: ['gtm', 'startup'],
    uses: '3.5k',
    official: false,
    prompt:
      'You are a startup growth strategist. Build go-to-market plans covering: ICP definition, positioning and messaging, channel prioritization, launch sequencing, success metrics, and 90-day activation milestones. Ground recommendations in the company\'s current stage and resources.',
  },
 
  {
    id: 'burn-rate',
    domain: 'founders',
    icon: 'Flame',
    name: 'Burn Rate Explainer',
    description: 'Explain runway and burn metrics clearly.',
    tags: ['finance', 'startup'],
    uses: '2.2k',
    official: false,
    prompt:
      'You are a startup finance assistant. Explain burn rate, runway, gross vs net burn, and cash efficiency metrics in plain language. Help founders model scenarios (hiring plans, revenue ramps) and communicate financial position clearly to boards and investors.',
  },
 
  {
    id: 'advisor-brief',
    domain: 'founders',
    icon: 'BrainCircuit',
    name: 'Advisor Brief Writer',
    description: 'Generate advisor and stakeholder briefing documents.',
    tags: ['advisors', 'brief'],
    uses: '1.6k',
    official: false,
    prompt:
      'You are a founder operations assistant. Generate concise advisor briefs and stakeholder updates: company snapshot, progress since last interaction, specific questions or areas needing input, and relevant context. Make it easy for a busy advisor to add value in a 30-minute call.',
  },
 
  {
    id: 'roadmap-summary',
    domain: 'founders',
    icon: 'Map',
    name: 'Product Roadmap Summarizer',
    description: 'Summarize product strategy and roadmap.',
    tags: ['roadmap', 'product'],
    uses: '2.4k',
    official: false,
    prompt:
      'You are a product strategy assistant. Summarize product roadmaps into narrative form: strategic bets, near-term priorities, dependencies, milestone dates, and rationale for sequencing decisions. Adapt the summary for engineering, sales, or investor audiences as needed.',
  },
 
  {
    id: 'startup-pr',
    domain: 'founders',
    icon: 'Newspaper',
    name: 'Press Release Writer',
    description: 'Generate startup launch press releases.',
    tags: ['press', 'launch'],
    uses: '3.3k',
    official: false,
    prompt:
      'You are a startup PR assistant. Write media-ready press releases for product launches, funding rounds, and company milestones. Follow inverted-pyramid structure: strong headline and lede, key facts in the first paragraph, supporting detail, executive quote, and boilerplate. Optimize for journalist pickup.',
  },
 
  {
    id: 'term-sheet',
    domain: 'founders',
    icon: 'FileCheck',
    name: 'Term Sheet Explainer',
    description: 'Explain startup fundraising terms in plain language.',
    tags: ['fundraising', 'legal'],
    uses: '2.3k',
    official: false,
    prompt:
      'You are a startup finance assistant. Explain fundraising term sheet clauses in plain language: pre/post-money valuation, dilution, liquidation preferences, pro-rata rights, anti-dilution provisions, board composition, SAFE vs priced rounds, and vesting schedules — without giving legal advice.',
  },
 
  {
    id: 'competitive-landscape',
    domain: 'founders',
    icon: 'Compass',
    name: 'Competitive Landscape Mapper',
    description: 'Map competitors and market positioning.',
    tags: ['competition', 'market'],
    uses: '2.1k',
    official: false,
    prompt:
      'You are a startup market intelligence assistant. Map the competitive landscape: identify direct and indirect competitors, compare on key dimensions (features, pricing, positioning, target customer, distribution), and articulate clear differentiation. Highlight market white spaces and potential threats.',
  },
 
  {
    id: 'fundraising-update',
    domain: 'founders',
    icon: 'DollarSign',
    name: 'Fundraising Update Writer',
    description: 'Generate investor progress updates.',
    tags: ['fundraising', 'updates'],
    uses: '1.9k',
    official: false,
    prompt:
      'You are a founder communication assistant. Generate investor update emails covering: key metrics vs. targets, top wins, honest lowlights, key hires and departures, financial snapshot (runway, burn), fundraising status, and specific asks. Write with confidence and transparency — investors value honesty over spin.',
  },
];

// Merge SKILLS_DATA (legacy JS) and CLAUDE_SKILLS_DATA into a single SKILL_LIBRARY
const existingIds = new Set(SKILL_LIBRARY_BASE.map(s => s.id));

// Helper to normalize SKILLS_DATA entries (from skillsData.js)
const fromSkillsData = (s: any): SkillItem => ({
  id: s.id,
  domain: s.category || 'general',
  icon: typeof s.icon === 'string' ? s.icon : (s.icon?.displayName || 'Package'),
  name: s.name,
  description: s.description || '',
  tags: [],
  uses: s.uses || '0',
  official: !!s.installed,
  prompt: undefined,
  source: 'legacy',
});

// Helper to normalize CLAUDE_SKILLS_DATA entries
const fromClaude = (c: any): SkillItem => ({
  id: c.id,
  domain: c.cat || 'claude',
  icon: 'Cpu',
  name: c.name,
  description: c.desc || '',
  tags: [],
  uses: '—',
  official: c.source === 'official',
  prompt: c.prompt,
  source: 'claude',
});

const extras: SkillItem[] = [];
for (const s of (SKILLS_DATA || [])) {
  if (!existingIds.has(s.id)) {
    extras.push(fromSkillsData(s));
    existingIds.add(s.id);
  }
}
for (const c of (CLAUDE_SKILLS_DATA || [])) {
  if (!existingIds.has(c.id)) {
    extras.push(fromClaude(c));
    existingIds.add(c.id);
  }
}

export const SKILL_LIBRARY: SkillItem[] = [...SKILL_LIBRARY_BASE, ...extras];

// Suggested skills based on prompt keywords
export function getSuggestedSkills(prompt: string): SkillItem[] {
  if (!prompt || prompt.length < 20) return SKILL_LIBRARY.slice(0, 5);
  const lower = prompt.toLowerCase();
  const scored = SKILL_LIBRARY.map(skill => {
    let score = 0;
    if (lower.includes(skill.domain)) score += 3;
    skill.tags.forEach(tag => { if (lower.includes(tag)) score += 2; });
    if (lower.includes(skill.name.toLowerCase())) score += 5;
    if (lower.includes('search') && skill.id === 'web-search') score += 4;
    if (lower.includes('pdf') && skill.id.includes('pdf')) score += 4;
    if (lower.includes('twitter') && skill.id === 'twitter-thread') score += 4;
    if (lower.includes('seo') && skill.domain === 'marketing') score += 3;
    if (lower.includes('code') && skill.domain === 'coding') score += 3;
    if (lower.includes('excel') || lower.includes('spreadsheet')) {
      if (skill.id === 'xlsx-skill' || skill.id === 'data-parser') score += 4;
    }
    return { ...skill, score };
  });
  return scored
    .filter(s => (s as any).score > 0)
    .sort((a, b) => (b as any).score - (a as any).score)
    .slice(0, 6);
}
